import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { useLocale } from "@/i18n/LocaleProvider";
import { LANGS, LANG_SHORT, LANG_LABELS, type Lang } from "@/i18n/locales";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Flag from "@/components/Flag";

/**
 * Language switcher showing the active language's flag. Clicking opens a fancy
 * popover listing all three languages (flag + name) to pick from. Navigates to
 * the equivalent page in the chosen language and persists the choice.
 */
const LanguageSwitcher = ({ className = "" }: { className?: string }) => {
  const { lang, switchLanguage } = useLocale();
  const [open, setOpen] = useState(false);

  const handleSelect = (code: Lang) => {
    setOpen(false);
    if (code !== lang) switchLanguage(code);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Language"
          className={`group inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card/40 px-2.5 py-1.5 transition-colors duration-200 hover:border-primary/50 hover:bg-card/70 ${className}`}
        >
          <Flag lang={lang} size={18} />
          <span className="font-mono text-[11px] tracking-[0.12em] text-foreground">
            {LANG_SHORT[lang]}
          </span>
          <ChevronDown
            className={`h-3 w-3 text-muted-foreground transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="z-[90] w-48 overflow-hidden rounded-xl border-border/60 bg-card/95 p-1.5 backdrop-blur-xl"
      >
        <div className="mb-1 px-2 pt-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            // language
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          {LANGS.map((code: Lang) => {
            const active = code === lang;
            return (
              <button
                key={code}
                type="button"
                onClick={() => handleSelect(code)}
                aria-current={active ? "true" : undefined}
                className={`group flex items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors duration-200 ${
                  active
                    ? "bg-primary/15 text-foreground"
                    : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                }`}
              >
                <Flag lang={code} size={22} />
                <span className="flex flex-1 flex-col">
                  <span className="text-sm font-medium leading-tight">
                    {LANG_LABELS[code]}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground">
                    {LANG_SHORT[code]}
                  </span>
                </span>
                {active && <Check className="h-4 w-4 text-primary" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LanguageSwitcher;
